import { AUTOPILOT } from '../config/tuning';
import { clamp } from './Aerodynamics';
import type { ControlInput, FlightState } from './FlightState';

/**
 * Autopilot assists (SPEC §3.2.6).
 *
 * Each mode writes into the control input *after* the player's, and any deliberate stick input
 * disengages it silently. Re-arming is always manual: an assist that re-engages on its own while
 * the player is manoeuvring in combat is the worst behaviour this system could have, so the only
 * path back to engaged is an explicit call.
 *
 * Angles in radians; the degree limits in config are converted once here.
 */

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const TWO_PI = Math.PI * 2;

const BANK_DISENGAGE_RAD = AUTOPILOT.bankDisengageDeg * DEG_TO_RAD;
const MAX_PITCH_RAD = AUTOPILOT.maxPitchDeg * DEG_TO_RAD;
const MAX_BANK_RAD = AUTOPILOT.maxBankDeg * DEG_TO_RAD;
const RECOVER_BANK_RAD = AUTOPILOT.recoverBankDeg * DEG_TO_RAD;

export const AutopilotMode = {
  Off: 'OFF',
  AltitudeHold: 'ALT',
  HeadingHold: 'HDG',
  AltitudeAndHeading: 'ALT+HDG',
} as const;

export type AutopilotModeLabel = (typeof AutopilotMode)[keyof typeof AutopilotMode];

/** Wrap an angle to (-π, π]. */
function wrapAngleRad(angleRad: number): number {
  let a = angleRad % TWO_PI;
  if (a > Math.PI) a -= TWO_PI;
  if (a <= -Math.PI) a += TWO_PI;
  return a;
}

export class Autopilot {
  altitudeHold = false;
  headingHold = false;

  /** Enabled on Training difficulty only. Levels the aircraft when inverted near the ground. */
  autoRecoverEnabled = false;
  autoRecoverActive = false;

  targetAltitudeM = 0;
  targetHeadingRad = 0;

  /** Engage altitude hold at the aircraft's current altitude. */
  engageAltitudeHold(state: FlightState): void {
    this.targetAltitudeM = state.altitudeM;
    this.altitudeHold = true;
  }

  /** Engage heading hold on the aircraft's current heading. */
  engageHeadingHold(state: FlightState): void {
    this.targetHeadingRad = state.headingRad;
    this.headingHold = true;
  }

  /** Engage both at the current altitude and heading. */
  engageAll(state: FlightState): void {
    this.engageAltitudeHold(state);
    this.engageHeadingHold(state);
  }

  disengageAll(): void {
    this.altitudeHold = false;
    this.headingHold = false;
    this.autoRecoverActive = false;
  }

  get engaged(): boolean {
    return this.altitudeHold || this.headingHold;
  }

  /** Label for the HUD. */
  get modeLabel(): AutopilotModeLabel {
    if (this.altitudeHold && this.headingHold) return AutopilotMode.AltitudeAndHeading;
    if (this.altitudeHold) return AutopilotMode.AltitudeHold;
    if (this.headingHold) return AutopilotMode.HeadingHold;
    return AutopilotMode.Off;
  }

  /**
   * Apply the engaged assists, mutating `input` in place. Call after sampling the player's
   * controls and before the flight model integrates (SPEC §4.3, step 4).
   */
  update(dtS: number, state: FlightState, input: ControlInput): void {
    void dtS; // the loops are proportional on measured rates; no integral term to advance yet

    this.applyBreakout(input);
    this.updateAutoRecover(state, input);
    if (this.autoRecoverActive) return;

    if (this.altitudeHold) {
      if (Math.abs(state.bankRad) > BANK_DISENGAGE_RAD) {
        // Past this bank, holding altitude means pulling g the player did not ask for.
        this.altitudeHold = false;
      } else {
        input.pitchCmd = this.altitudeHoldPitchCommand(state);
      }
    }

    if (this.headingHold) {
      input.rollCmd = this.headingHoldRollCommand(state);
    }
  }

  /**
   * Pitch command that holds `targetAltitudeM`. Exposed for tests and the M2 HUD.
   *
   * A three-stage cascade: altitude error → commanded climb rate → commanded flight-path angle →
   * pitch attitude → pitch command. The middle conversion goes through the *flight path* and
   * adds α to reach an attitude, rather than nudging the current attitude by an error term.
   * Nudging the current value turns the loop into an integrator, and an integrator wrapped
   * around an aircraft's own lag porpoises — visibly, and forever.
   */
  altitudeHoldPitchCommand(state: FlightState): number {
    const altitudeErrorM = this.targetAltitudeM - state.altitudeM;
    let commandedClimbMs = clamp(
      altitudeErrorM * AUTOPILOT.altitudeToClimbRate,
      -AUTOPILOT.maxClimbRateMs,
      AUTOPILOT.maxClimbRateMs,
    );

    // Angle-of-attack protection. Without it the loop asks for a climb rate the engine cannot
    // sustain, and the aircraft buys the difference with airspeed until it stalls at the top of
    // the climb — an assist that departs the aircraft is worse than no assist. Only the climb is
    // limited: lowering the nose is always available to an aircraft that is slow.
    if (commandedClimbMs > 0) {
      const alphaDeg = state.alphaRad * RAD_TO_DEG;
      const alphaHeadroom = clamp(
        (AUTOPILOT.alphaLimitDeg - alphaDeg) / AUTOPILOT.alphaTaperDeg,
        0,
        1,
      );
      const speedHeadroom = clamp(
        (state.airspeedMs - AUTOPILOT.climbSpeedFloorMs) / AUTOPILOT.climbSpeedTaperMs,
        0,
        1,
      );
      commandedClimbMs *= Math.min(alphaHeadroom, speedHeadroom);
    }

    const speedMs = Math.max(state.airspeedMs, 1);
    const commandedPathRad = Math.asin(clamp(commandedClimbMs / speedMs, -1, 1));
    const commandedPitchRad = clamp(
      commandedPathRad + state.alphaRad,
      -MAX_PITCH_RAD,
      MAX_PITCH_RAD,
    );

    const pitchErrorRad = commandedPitchRad - state.pitchRad;
    return clamp(
      pitchErrorRad * AUTOPILOT.pitchToCommand - state.pitchRateRadS * AUTOPILOT.pitchRateDamping,
      -1,
      1,
    );
  }

  /** Roll command that holds `targetHeadingRad`. */
  headingHoldRollCommand(state: FlightState): number {
    const headingErrorRad = wrapAngleRad(this.targetHeadingRad - state.headingRad);
    const commandedBankRad = clamp(
      headingErrorRad * AUTOPILOT.headingToBank,
      -MAX_BANK_RAD,
      MAX_BANK_RAD,
    );
    const bankErrorRad = commandedBankRad - state.bankRad;
    return clamp(
      bankErrorRad * AUTOPILOT.bankToCommand - state.rollRateRadS * AUTOPILOT.rollRateDamping,
      -1,
      1,
    );
  }

  /** Any deliberate stick input hands control back to the player, silently. */
  private applyBreakout(input: ControlInput): void {
    const threshold = AUTOPILOT.inputBreakoutThreshold;
    if (Math.abs(input.pitchCmd) > threshold) this.altitudeHold = false;
    if (Math.abs(input.rollCmd) > threshold || Math.abs(input.yawCmd) > threshold) {
      this.headingHold = false;
    }
  }

  /**
   * AUTO-RECOVER: roll upright and pull level when inverted below the recovery height.
   * Training difficulty only, and it releases as soon as the aircraft is upright again.
   */
  private updateAutoRecover(state: FlightState, input: ControlInput): void {
    if (!this.autoRecoverEnabled) {
      this.autoRecoverActive = false;
      return;
    }

    const inverted = Math.abs(state.bankRad) > RECOVER_BANK_RAD;
    const low = state.altitudeM < AUTOPILOT.recoverAltitudeM;
    if (inverted && low) this.autoRecoverActive = true;
    if (!inverted) this.autoRecoverActive = false;
    if (!this.autoRecoverActive) return;

    // Roll to wings level, and unload rather than pull while still inverted.
    input.rollCmd = clamp(
      -state.bankRad * AUTOPILOT.bankToCommand - state.rollRateRadS * AUTOPILOT.rollRateDamping,
      -1,
      1,
    );
    input.pitchCmd = clamp(-state.pitchRad * AUTOPILOT.pitchToCommand, -1, 1);
  }
}
